import type { Institution } from '../domain/entities/content.entity';
import { formatInstitutionLabel } from './exam-body';

/**
 * O CSV modelo da importação, montado no browser.
 *
 * Existe pelo mesmo motivo que a listagem mostra a instituição no formato do
 * arquivo: o formato deixa de ser algo que alguém precisa lembrar de digitar
 * certo. É gerado (e não servido como asset estático) para não virar uma
 * segunda fonte de verdade — a coluna `institution` sai do mesmo
 * `formatInstitutionLabel` que a tela usa, então mudar a gramática num lugar
 * não deixa um arquivo mentindo no outro.
 *
 * O formato das colunas está em
 * `primum-core-root/docs/plans/[GEOVANNI] exam-csv-import-plan/csv-format.md`.
 */

const HEADER = [
  'position',
  'institution',
  'exam_year',
  'exam_variant',
  'exam_type',
  'question_format',
  'stem',
  'alt_a_text',
  'alt_b_text',
  'alt_c_text',
  'alt_d_text',
  'alt_e_text',
  'correct_alternative',
  'classification',
  'comment',
  'comment_author',
  'expected_answer',
] as const;

/** Aspas duplicadas e célula entre aspas — quoting RFC4180, que o parser espera. */
function cell(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

/**
 * Três linhas de exemplo: uma questão simples, uma com imagem e enunciado de
 * várias linhas — que é justamente o que ninguém acerta de primeira — e uma
 * dissertativa com resposta esperada.
 *
 * Os assuntos do exemplo são nomes que **existem** na taxonomia importada do
 * acervo (`depara-classificacao.json` do import, no monolith): o modelo cru tem
 * de passar pelo dry-run, e um nome inventado o fazia parar em "assunto não
 * encontrado" — quem testava o importador com o próprio modelo via erro. Estão
 * escritos aqui, e não lidos do cadastro como a instituição, porque a taxonomia
 * tem centenas de nós e não há "o primeiro" que sirva de exemplo. O importador
 * bloqueia nome não cadastrado, então se um deles sair da taxonomia o erro
 * aparece na validação, antes de qualquer escrita.
 */
export function buildExamCsvTemplate(
  institutionsById: ReadonlyMap<string, Institution> | undefined,
): string {
  // A primeira instituição **não arquivada**: o cache de rótulo inclui as
  // arquivadas de propósito (é ele que resolve a coluna "Instituição" das
  // provas antigas), e uma arquivada não resolve mais na importação
  // (`findByInitials` filtra as vivas) — o modelo ensinaria uma sigla que o
  // importador recusa com `NOT_FOUND`.
  const first = [...(institutionsById?.values() ?? [])].find(
    (i) => i.deletedAt === null,
  );
  const institution = first
    ? formatInstitutionLabel(first.id, institutionsById)
    : '(SIGLA-UF) Nome da instituição';
  const year = String(new Date().getFullYear());
  const variant = 'Clínica Médica';

  const rows = [
    [
      '1',
      institution,
      year,
      variant,
      // A trilha aceita a forma que o material de origem usa ("Acesso Direto",
      // "R1") além do valor canônico — o modelo mostra a mais legível.
      'Acesso Direto',
      'MULTIPLE_CHOICE',
      'Paciente de 54 anos com dor torácica há 2 horas. Qual a conduta inicial?',
      'Solicitar ECG em até 10 minutos',
      'Iniciar antibiótico',
      'Alta com analgésico',
      '',
      '',
      'A',
      // Um assunto só: a classificação secundária é opcional, e o exemplo mais
      // simples não deve sugerir que sempre se preenchem duas.
      'Aterosclerose e arteriosclerose',
      'O ECG em até 10 minutos é o que define conduta na dor torácica aguda.',
      'Equipe Primum',
      // Múltipla escolha não tem resposta esperada — preencher é erro da linha.
      '',
    ],
    [
      '2',
      institution,
      year,
      variant,
      'Acesso Direto',
      'MULTIPLE_CHOICE',
      // Quebra de linha dentro da célula e token de imagem, os dois casos que
      // o formato aceita e que a planilha "limpa" costuma não ter.
      'Observe a tomografia abaixo:\n\n[](imagens/q2-tc.png)\n\nQual o diagnóstico?',
      'Pancreatite aguda edematosa',
      'Pancreatite necrosante',
      'Pancreatite crônica',
      '[](imagens/q2-alt-d.png)',
      '',
      'B',
      // Primária + secundária, na ordem: o primeiro nome é a classificação
      // primária da questão, os seguintes são secundárias.
      'Pancreatite | Pancreatite necrosante',
      // O comentário aceita o MESMO token de imagem do enunciado — é o que o
      // exemplo precisa mostrar, porque ninguém supõe isso sozinho.
      //
      // Com um arquivo PRÓPRIO, e não o `q2-tc.png` do enunciado, de
      // propósito: questão e comentário são content items distintos e cada um
      // é dono das suas imagens, então citar o mesmo arquivo nos dois sobe os
      // mesmos bytes duas vezes. Funciona, mas o modelo não deve ensinar isso.
      'Repare nas áreas sem realce do parênquima:\n\n[](imagens/q2-comentario.png)\n\nÉ necrose pancreática.',
      'Equipe Primum',
      '',
    ],
    [
      '3',
      institution,
      year,
      variant,
      'Acesso Direto',
      // A dissertativa: sem alternativas nem gabarito, e com a resposta
      // esperada — a única coluna que só ela usa.
      'ESSAY',
      'Cite três sinais de alarme da dengue.',
      '',
      '',
      '',
      '',
      '',
      '',
      'Arboviroses',
      '',
      '',
      'Dor abdominal intensa e contínua, vômitos persistentes e sangramento de mucosa.',
    ],
  ];

  return [HEADER.join(','), ...rows.map((row) => row.map(cell).join(','))].join(
    '\n',
  );
}

/**
 * Baixa um texto como arquivo. **BOM UTF-8 na frente**: sem ele o Excel abre
 * "Ciências Médicas" como "CiÃªncias MÃ©dicas" e a planilha volta corrompida
 * para o importador. O parse do import já remove o BOM na volta.
 */
export function downloadTextFile(filename: string, content: string): void {
  const blob = new Blob(['﻿', content], {
    type: 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
